import React from 'react'
import axios from 'axios'
import moment from 'moment'
import { RefreshCw, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import toastr from '@/lib/notifications'
import Modal from '../../modal'
import CancelCustomerOrder from './cancel'
import PromoBadge from '../../promo_badge'
import store from '../../../state/store'

const TYPE_LABELS = {
  coachbit: 'Coachbit',
  classrooms: 'Classrooms',
  bootcamps: 'Bootcamp',
}

class EnquiryCustomerOrders extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      orders: [],
      loaded: false,
      cancelling: null,
    }

    this.fetchData = this.fetchData.bind(this)
    this.onCancelled = this.onCancelled.bind(this)
    this.onCloseCancel = this.onCloseCancel.bind(this)
  }

  serviceIdFor(order) {
    if (order.type === 'coachbit') {
      return `cbo${order.id}`
    }

    return `co${order.id}`
  }

  cancellable(order) {
    return ['placing', 'placed'].includes(order.workflow_state) && !order.paid
  }

  onCancelled() {
    this.setState({ cancelling: null })
    this.fetchData()
  }

  onCloseCancel() {
    this.setState({ cancelling: null })
  }

  componentDidMount() {
    this.fetchData()
    store.on('customer_orders:refresh', this.fetchData)
  }

  componentWillUnmount() {
    store.off('customer_orders:refresh', this.fetchData)
  }

  fetchData() {
    axios.get(`/api/sales/enquiries/${this.props.enquiry_id}/customer_orders`)
      .then((response) => {
        this.setState({ orders: response.data.orders, loaded: true })
      })
      .catch((error) => {
        const message = (error && error.response && error.response.data && error.response.data.custom_error_message) || 'Failed to load customer orders.'
        toastr.error(message)
      })
  }

  // Same states as the learner card: a Coachbit order follows the subscription it provisioned.
  renderState(order) {
    const subscription = order.subscription

    if (order.workflow_state === 'cancelled') {
      return <Badge variant="secondary">Cancelled</Badge>
    }

    if (subscription && subscription.workflow_state === 'cancelled') {
      return (
        <Badge variant="secondary">
          Cancelled{subscription.cancelled_at && ` ${moment(subscription.cancelled_at).format('DD/MM/YYYY')}`}
        </Badge>
      )
    }

    if (subscription && subscription.trial_state === 'in_trial') {
      return <Badge variant="info">Trial in progress</Badge>
    }

    if (subscription && subscription.trial_state === 'lapsed') {
      return <Badge variant="secondary">Trial ended, awaiting payment</Badge>
    }

    if (order.trial_months > 0 && order.workflow_state === 'placed' && !order.paid) {
      return <Badge variant="outline" title="The customer activates this trial from their customer dashboard">Awaiting activation</Badge>
    }

    if (order.workflow_state === 'provisioned') {
      return <Badge>Provisioned</Badge>
    }

    if (order.paid) {
      return <Badge>Paid</Badge>
    }

    return <Badge variant="outline">Awaiting payment</Badge>
  }

  render() {
    if (!this.state.loaded || this.state.orders.length === 0) {
      return null
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Customer Orders</CardTitle>
          <CardAction>
            <Button size="icon-sm" variant="outline" title="Refresh" onClick={this.fetchData}>
              <RefreshCw />
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">
            Coachbit, Classroom and Bootcamp orders for this customer. Classroom and Bootcamp orders are standalone and are paid via the customer dashboard.
          </p>
          <div className="flex flex-col gap-2">
            {this.state.orders.map((order) => (
              <div key={`${order.type}-${order.id}`} className="flex flex-wrap items-center gap-3 rounded-md border p-2 text-sm">
                <span className="text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</span>
                <span className="font-medium">
                  {TYPE_LABELS[order.type] || order.type}
                  {order.type === 'coachbit' && ` (${order.checkin_days} check-ins/week)`}
                  {order.trial_months > 0 && ' (Trial)'}
                </span>
                <span>{order.learner_name}</span>
                <PromoBadge promo={order.promo} />
                <span className="ml-auto flex items-center gap-2">
                  {this.renderState(order)}
                  {this.cancellable(order) && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      title="Cancel order"
                      onClick={() => this.setState({ cancelling: order })}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </CardContent>

        {this.state.cancelling && (
          <Modal
            title={`Cancel ${TYPE_LABELS[this.state.cancelling.type] || this.state.cancelling.type} Order`}
            onClose={this.onCloseCancel}
          >
            <div className="p-6">
              <CancelCustomerOrder
                enquiryId={this.props.enquiry_id}
                serviceId={this.serviceIdFor(this.state.cancelling)}
                onCancelled={this.onCancelled}
                onClose={this.onCloseCancel}
              />
            </div>
          </Modal>
        )}
      </Card>
    )
  }
}

export default EnquiryCustomerOrders
